// Globals
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { ChannelType } from '@/models/channel.model';

// Modules
import { selectChannel } from '@/modules/Channels';
import { Header } from '@/modules/Layout';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Components
import { Spinner } from '@/ui';
import { useForm } from '@/components';
import { LLMConfigForm } from '../../components';
import { StyledContainer } from './LLMConfigContainerStyles';

// Helpers
import { useGetLLMConfig } from '../../hooks';
import { getLLMConfigInitialValues, TLLMConfigFormValues } from '../../helpers';
import { updateLLMConfig } from '../../feature';

const LLMConfigContainer = () => {
  const { t } = useTranslation();
  const channel = useAppState(selectChannel);
  const dispatch = useAppDispatch();
  const { isLoading, llmConfig } = useGetLLMConfig({ channelId: channel?.id });
  const [formInstance] = useForm();

  const initialValues = useMemo(() => getLLMConfigInitialValues(llmConfig), [llmConfig]);

  useEffect(() => {
    if (llmConfig) {
      formInstance.setFieldsValue(getLLMConfigInitialValues(llmConfig));
    }
  }, [llmConfig, formInstance]);

  const handleUpdateLLMConfig = async (values: TLLMConfigFormValues) => {
    if (!channel) return;

    try {
      await dispatch(updateLLMConfig({ ...values, channelId: channel.id })).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const showAttributes = !!channel?.type && channel.type !== ChannelType.SHOWCASE;

  return (
    <>
      <Header title={t('menu_llm_config')} suptitle={t('menu_seo')} />
      <Spinner spinning={isLoading}>
        <StyledContainer>
          <LLMConfigForm
            form={formInstance}
            showAttributes={showAttributes}
            onSubmit={handleUpdateLLMConfig}
            initialValues={initialValues}
            isLoading={isLoading}
          />
        </StyledContainer>
      </Spinner>
    </>
  );
};

export default LLMConfigContainer;
